import React, { useEffect, useMemo, useState } from "react";
import { PageShell, EditableSection } from "./PageShell";
import { Sparkles, Gift, Info, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PRESETS = [10, 30, 50, 100, 250];
const CUSTOM_MIN = 5;
const CUSTOM_MAX = 500;

const HOW = [{
  heading: "How Scentodore gift cards work",
  body: "Choose a preset amount or type your own. Complete checkout as you would any Scentodore order. The gift card is delivered by email — to you, or directly to the recipient at a date you choose. Cards never expire and can be used across the entire Scentodore collection.",
}];

const FINE_PRINT = [{
  heading: "Fine print",
  body: "Gift cards are issued in Canadian dollars. Balance can be applied to any order across Scentodore. Cards are non-refundable and cannot be exchanged for cash except where required by law. Codes are delivered by email within a few minutes of purchase (custom-scheduled delivery lets you pick any future date).",
}];

export default function GiftCardsPage() {
  const [status, setStatus] = useState(null); // { enabled, presets: {10: true, ...}, custom_supported }
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [recipient, setRecipient] = useState({ name: "", email: "", sender: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/gift-cards/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ enabled: false, presets: {}, custom_supported: false }));
  }, []);

  const effectiveAmount = useMemo(() => {
    if (custom) {
      const n = parseInt(custom, 10);
      if (!Number.isNaN(n) && n >= CUSTOM_MIN && n <= CUSTOM_MAX) return n;
      return null;
    }
    return amount;
  }, [amount, custom]);

  const enabled = status?.enabled === true;
  const canPurchase = enabled && effectiveAmount !== null;

  const onCheckout = async (e) => {
    e.preventDefault();
    if (!canPurchase) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/gift-cards/checkout", {
        amount: effectiveAmount,
        recipient_email: recipient.email || null,
        recipient_name: recipient.name || null,
        sender_name: recipient.sender || null,
        message: recipient.message || null,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      toast.error("We couldn't complete gift-card checkout. Please email us and we'll help right away.");
      setSubmitting(false);
    }
  };

  return (
    <PageShell
      slug="gift-cards"
      testid="gift-cards"
      eyebrow="Gift Cards"
      title="A signature they choose themselves."
      intro="A Scentodore gift card — delivered by email, redeemable across the entire collection. Pick a preset amount or set your own."
    >
      {/* Status banner if not yet enabled */}
      {status && !enabled ? (
        <div className="mb-10 flex items-start gap-3 p-5 rounded-2xl border border-[#C5A059]/40 bg-[#F1ECE4]/50" data-testid="gift-cards-coming-soon">
          <Info className="h-5 w-5 text-[#C5A059] mt-0.5 shrink-0" />
          <div className="text-sm text-[#1A1918]/80 leading-[1.7]">
            <div className="font-serif text-base text-[#1A1918] mb-1">Digital gift cards launching soon</div>
            We're finalising our gift-card system. In the meantime, please <a href="/contact" className="text-[#C5A059] hover:underline">contact our concierge</a> and we'll set up a bespoke gift card by hand.
          </div>
        </div>
      ) : null}

      {/* Denomination picker */}
      <form onSubmit={onCheckout} className="mb-14" data-testid="gift-card-form">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-12">
          {/* Left column — amount + recipient */}
          <div className="space-y-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-3">Step 01 · Choose amount</div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                {PRESETS.map((p) => {
                  const active = !custom && amount === p;
                  const supported = status ? (status.presets?.[p] === true || status.custom_supported === true) : true;
                  return (
                    <button
                      key={p}
                      type="button"
                      disabled={!supported && !status?.custom_supported}
                      onClick={() => { setAmount(p); setCustom(""); }}
                      data-testid={`gift-card-preset-${p}`}
                      className={`h-14 rounded-xl border font-serif text-lg transition-all ${
                        active
                          ? "border-[#1A1918] bg-[#1A1918] text-[#FAF8F5] shadow-md"
                          : supported
                          ? "border-black/10 bg-[#FAF8F5] text-[#1A1918] hover:border-[#C5A059] hover:text-[#C5A059]"
                          : "border-black/5 bg-[#F1ECE4]/40 text-[#1A1918]/30 cursor-not-allowed"
                      }`}
                    >
                      ${p}
                    </button>
                  );
                })}
              </div>

              <div className="mt-4">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">Or a custom amount (CA${CUSTOM_MIN}–${CUSTOM_MAX})</span>
                  <div className="mt-2 relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 font-serif text-lg text-[#1A1918]/60">$</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={CUSTOM_MIN}
                      max={CUSTOM_MAX}
                      step="1"
                      value={custom}
                      onChange={(e) => { setCustom(e.target.value.replace(/[^\d]/g, "")); }}
                      placeholder="e.g. 75"
                      data-testid="gift-card-custom-amount"
                      className="w-full pl-9 pr-4 py-3.5 rounded-xl border border-black/10 bg-[#FAF8F5] text-[15px] focus:outline-none focus:border-[#C5A059]"
                    />
                  </div>
                  {custom && effectiveAmount === null ? (
                    <div className="mt-1.5 text-xs text-red-600/80">Please enter a whole amount between CA${CUSTOM_MIN} and CA${CUSTOM_MAX}.</div>
                  ) : null}
                </label>
              </div>
            </div>

            {/* Recipient details */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-3">Step 02 · Delivery (optional)</div>
              <p className="text-sm text-[#1A1918]/70 mb-4">Leave blank to receive the gift card at your own email — perfect if you want to add a personal note when you hand it over.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.16em] text-[#1A1918]/70">Recipient name</span>
                  <input value={recipient.name} onChange={(e) => setRecipient((r) => ({ ...r, name: e.target.value }))} data-testid="gift-card-recipient-name" className="mt-1 w-full px-4 py-3 rounded-xl border border-black/10 bg-[#FAF8F5] text-sm focus:outline-none focus:border-[#C5A059]" />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.16em] text-[#1A1918]/70">Recipient email</span>
                  <input type="email" value={recipient.email} onChange={(e) => setRecipient((r) => ({ ...r, email: e.target.value }))} data-testid="gift-card-recipient-email" className="mt-1 w-full px-4 py-3 rounded-xl border border-black/10 bg-[#FAF8F5] text-sm focus:outline-none focus:border-[#C5A059]" />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.16em] text-[#1A1918]/70">Your name (sender)</span>
                  <input value={recipient.sender} onChange={(e) => setRecipient((r) => ({ ...r, sender: e.target.value }))} data-testid="gift-card-sender" className="mt-1 w-full px-4 py-3 rounded-xl border border-black/10 bg-[#FAF8F5] text-sm focus:outline-none focus:border-[#C5A059]" />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.16em] text-[#1A1918]/70">Personal message</span>
                  <input value={recipient.message} onChange={(e) => setRecipient((r) => ({ ...r, message: e.target.value }))} maxLength={200} placeholder="Optional" data-testid="gift-card-message" className="mt-1 w-full px-4 py-3 rounded-xl border border-black/10 bg-[#FAF8F5] text-sm focus:outline-none focus:border-[#C5A059]" />
                </label>
              </div>
            </div>
          </div>

          {/* Right column — summary card */}
          <aside className="lg:sticky lg:top-8 self-start">
            <div className="p-6 sm:p-8 rounded-2xl border border-black/10 bg-[#F1ECE4]/40">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-4">
                <Gift className="h-3.5 w-3.5" /> Order summary
              </div>
              <div className="flex items-center justify-between mb-2 text-sm text-[#1A1918]/70">
                <span>Scentodore Gift Card</span>
              </div>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="font-serif text-4xl sm:text-5xl text-[#1A1918]">
                  ${effectiveAmount ?? "—"}
                </span>
                <span className="text-sm text-[#1A1918]/60">CAD</span>
              </div>
              <ul className="space-y-2 text-[13.5px] text-[#1A1918]/75 mb-6">
                <li className="flex items-start gap-2"><Sparkles className="h-3.5 w-3.5 mt-1 text-[#C5A059] shrink-0" /> Delivered instantly by email</li>
                <li className="flex items-start gap-2"><Sparkles className="h-3.5 w-3.5 mt-1 text-[#C5A059] shrink-0" /> Redeemable across the whole collection</li>
                <li className="flex items-start gap-2"><Sparkles className="h-3.5 w-3.5 mt-1 text-[#C5A059] shrink-0" /> Never expires</li>
              </ul>
              <button
                type="submit"
                disabled={!canPurchase || submitting}
                data-testid="gift-card-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1918] text-[#FAF8F5] px-5 py-4 rounded-xl text-xs uppercase tracking-[0.18em] hover:bg-[#C5A059] hover:text-[#0B0A08] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</>
                ) : (
                  <>Continue to checkout <ArrowRight className="h-4 w-4" /></>
                )}
              </button>
              {!enabled && status ? (
                <div className="mt-3 text-[11px] text-[#1A1918]/50 text-center">
                  Purchase temporarily disabled — <a href="/contact" className="text-[#C5A059] hover:underline">contact us</a> to buy a card manually.
                </div>
              ) : null}
            </div>
          </aside>
        </div>
      </form>

      <EditableSection slug="gift-cards" sectionKey="how" fallback={HOW} />
      <EditableSection slug="gift-cards" sectionKey="fine_print" fallback={FINE_PRINT} />

      <div className="-mt-8 pt-2 border-t border-black/5">
        <a
          href="/gift-cards/balance"
          data-testid="gift-cards-balance-link"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-[#1A1918]/60 hover:text-[#C5A059] mt-6"
        >
          Already have a card? Check your balance <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>
    </PageShell>
  );
}
